<script setup>
  import {computed, inject, nextTick, onMounted, onUpdated, provide, ref} from "vue";

  import LayoutAuthHeader from "@components/Layout/Components/AuthHeader/AuthHeader.vue";
  import LayoutDialogCrypt from "@components/Layout/Components/CryptDialog.vue";
  import LayoutFooter from "@components/Layout/Components/Footer.vue";
  import LayoutGuestHeader from "@components/Layout/Components/GuestHeader.vue";
  import ComponentMessage from "@components/Messages/Message.vue";
  import ComponentToastMessage from "@components/Messages/ToastMessage.vue";
  import {Head as InertiaHead, router, usePage} from "@inertiajs/vue3";
  import {cn} from "@pharma4u/medicheck-components/utils";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";

  defineOptions({
    name: "ComponentLayout",
  });

  defineProps({
    title: {
      type: String,
      default: null,
    },
  });
  const page = usePage();

  const privacy = inject("$privacy");

  const componentToastMessage = ref(null);

  const hasClient = computed(() => !!page.props.client);
  const isAuthenticated = computed(() => {
    return (
      hasClient.value &&
      privacy.getCurrentUser() !== null &&
      (privacy.hasCryptStatus("VERIFICATION_VALID") || privacy.hasCryptStatus("RECOVERY_VALID"))
    );
  });

  onMounted(() => {
    layoutMessages();

    setInterval(() => {
      authRefresh();
    }, 300_000);
  });

  onUpdated(() => {
    layoutMessages();
  });

  const authRefresh = () => {
    if (page.props.client) {
      useAuthHttp()
        .post(route("api.auth.refresh"))
        .then((response) => {
          if (response?.error?.length > 0) {
            addToastMessage(response.error, "error");
            router.visit(route("login"));
          }
        })
        .catch(() => {
          addToastMessage("Etwas ist schiefgelaufen, bitte loggen Sie sich erneut ein.", "error");
          router.visit(route("login"));
        });
    }
  };

  const layoutMessages = () => {
    if (!page.props.flash) {
      return;
    }

    const flashKeys = ["success", "info", "warning", "error"];
    const timer = {
      success: 10_000,
      info: 15_000,
      warning: 20_000,
      error: 60_000,
    };

    for (const key of flashKeys) {
      if (page.props.flash[key]) {
        addToastMessage(page.props.flash[key], key, timer[key]);
        nextTick(() => {
          page.props.flash[key] = null;
        });
      }
    }
  };

  const addToastMessage = (message, type = "info", duration = 60_000) => {
    if (componentToastMessage.value) {
      componentToastMessage.value.addToastMessage(message, type, duration);
    } else {
      console.error("ToastMessage ref not available to show this: " + message);
    }
  };

  provide("addToastMessage", addToastMessage);
</script>

<template>
  <ComponentToastMessage ref="componentToastMessage" class="print:hidden" test-id="layout" />

  <LayoutDialogCrypt v-if="$page.props.onboarding || hasClient || isAuthenticated" ref="crypt" test-id="layout" />

  <InertiaHead :title="title" class="print:hidden" />

  <LayoutAuthHeader v-if="isAuthenticated" class="print:hidden" />

  <LayoutGuestHeader v-else class="print:hidden" />

  <main
    id="container"
    :class="
      cn('flex grow flex-col justify-self-center px-2 pt-4 pb-8 md:px-4 print:max-w-none print:p-0', {
        'pb-0': $page.props.title === 'Login',
        'mx-auto w-full max-w-11xl': $page.props.title !== 'Login',
      })
    "
    data-test="layout-page-content"
  >
    <div v-show="isAuthenticated">
      <ComponentMessage display-mode="all_pages" test-id="layout" />
      <slot />
    </div>

    <template v-if="!isAuthenticated && !hasClient">
      <slot />
    </template>
  </main>

  <LayoutFooter class="print:hidden" />
</template>
